import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgOption, NgSelectComponent, NgSelectConfig } from '@ng-select/ng-select';
import {
  addHours,
  endOfMonth,
  endOfToday,
  endOfYesterday,
  startOfDay,
  startOfMonth,
  startOfToday,
  startOfYesterday,
  subDays,
  subMonths,
} from 'date-fns';
import { DateString } from 'src/app/core/types/date-string';
import { SelectOption } from '../sky-select/sky-select.component';
import { dateToValidation, timeValidation } from './date-select-validators';

export type DateRange = { dateTo?: DateString; dateFrom?: DateString };

@Component({
  selector: 'sky-date-select',
  templateUrl: './date-select.component.html',
  styles: [
    `
      :host {
        display: block;
        position: relative;
      }

      label {
        display: flex;
        align-items: center;
      }

      input {
        text-align: center;
        border-width: 1px;
        border-radius: 0.75rem;
        @apply focus:border-sky-blue border-sky-grey  focus:outline-none py-2 px-3;
      }

      .date__block {
        width: 50%;
        max-width: 175px;
      }

      input[type='date'] {
        padding-right: 0;
        padding-left: 0;
        position: relative;
      }

      input[type='date']::-webkit-calendar-picker-indicator {
        background: transparent;
        width: 100%;
        position: absolute;
        margin: 0;
      }

      input[type='date']::-webkit-datetime-edit {
        display: flex;
        justify-content: center;
      }
    `,
  ],
})
export class DateSelectComponent<T> implements OnInit {
  @Input() public control!: FormControl;
  @Input() public smallSized = false;
  @Input() public placeholder = 'Kliknij i wybierz z listy';
  @Output() public valueChange = new EventEmitter();
  @ViewChild('ngSelect') public ngSelect!: NgSelectComponent;

  public options!: Array<SelectOption>;
  public dateControl = new FormControl('THIS_MONTH');
  public customDateForm!: FormGroup;
  public label = 'Data';
  public focused = false;
  public shouldTestInput = true;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private readonly DIFF_BETWEEN_GMT_ISO = 2;

  private readonly _defaultOptions: Array<SelectOption> = [
    {
      id: 'THIS_MONTH',
      label: 'Bieżący miesiąc',
    },
    {
      id: 'PREVIOUS_MONTH',
      label: 'Poprzedni miesiąc',
    },
    {
      id: 'TODAY',
      label: 'Dzisiaj',
    },
    {
      id: 'YESTERDAY',
      label: 'Wczoraj',
    },
    {
      id: 'LAST_7_DAYS',
      label: 'Ostatnie 7 dni',
    },
    {
      id: 'LAST_30_DAYS',
      label: 'Ostatnie 30 dni',
    },
    {
      id: 'CUSTOM',
      label: 'Zakres dat:',
      disabled: true,
    },
  ];

  constructor(private readonly _config: NgSelectConfig) {
    this._config.notFoundText = 'Nie znaleziono wyników';
    this._config.bindValue = 'id';
    this._config.bindLabel = 'label';
  }

  public ngOnInit() {
    this.options = [...this._defaultOptions];
    this.customDateForm = this.createCustomDateForm();
  }

  public getGroup(groupName: string): FormGroup {
    return this.customDateForm.get(groupName) as FormGroup;
  }

  public getControl(groupName: string, controlName: string): FormControl {
    return this.customDateForm.get(groupName)!.get(controlName) as FormControl;
  }

  public setCustomDate() {
    this.setCustomDateCorrectLabel();
    this.ngSelect.select(this.options.find(opt => opt.id === 'CUSTOM') as NgOption);
    this.ngSelect.close();

    const { dateFrom, dateTo } = this.customDateForm.value;

    if (!dateFrom.date && !dateTo.date) {
      this.control.setValue(null);
    }

    const dateRange: DateRange = {} as DateRange;

    if (dateFrom.date) {
      dateRange.dateFrom = this.createFormDateAsISOString(dateFrom);
    }

    if (dateTo.date) {
      dateRange.dateTo = this.createFormDateAsISOString(dateTo);
    }

    this.control.setValue(dateRange);
  }

  public handleChange(option: SelectOption) {
    const now = new Date();

    if (!option) {
      return;
    }

    switch (option.id) {
      case 'THIS_MONTH': {
        this.control.setValue({
          dateFrom: addHours(startOfMonth(now), this.DIFF_BETWEEN_GMT_ISO),
          dateTo: addHours(endOfMonth(now), this.DIFF_BETWEEN_GMT_ISO),
        });
        return;
      }

      case 'PREVIOUS_MONTH': {
        this.control.setValue({
          dateFrom: addHours(startOfMonth(subMonths(now, 1)), this.DIFF_BETWEEN_GMT_ISO),
          dateTo: addHours(endOfMonth(subMonths(now, 1)), this.DIFF_BETWEEN_GMT_ISO),
        });
        return;
      }

      case 'TODAY': {
        this.control.setValue({
          dateFrom: addHours(startOfToday(), this.DIFF_BETWEEN_GMT_ISO),
          dateTo: addHours(endOfToday(), this.DIFF_BETWEEN_GMT_ISO),
        });
        return;
      }

      case 'YESTERDAY': {
        this.control.setValue({
          dateFrom: addHours(startOfYesterday(), this.DIFF_BETWEEN_GMT_ISO),
          dateTo: addHours(endOfYesterday(), this.DIFF_BETWEEN_GMT_ISO),
        });
        return;
      }

      case 'LAST_7_DAYS': {
        this.control.setValue({
          dateFrom: addHours(startOfDay(subDays(now, 7)), this.DIFF_BETWEEN_GMT_ISO),
          dateTo: addHours(endOfToday(), this.DIFF_BETWEEN_GMT_ISO),
        });
        return;
      }

      case 'LAST_30_DAYS': {
        this.control.setValue({
          dateFrom: addHours(startOfDay(subDays(now, 30)), this.DIFF_BETWEEN_GMT_ISO),
          dateTo: addHours(endOfToday(), this.DIFF_BETWEEN_GMT_ISO),
        });
        return;
      }
    }
  }

  private setCustomDateCorrectLabel() {
    this.options = this._defaultOptions.map(opt => {
      if (opt.id === 'CUSTOM') {
        const { dateFrom, dateTo } = this.customDateForm.value;
        let label = '';

        if (!dateFrom.date && !dateTo.date) {
          label = 'Cały zakres';
        }

        if (dateFrom.date) {
          label += 'od ' + dateFrom.date + ' ' + (dateFrom.time || '');
        }

        if (dateTo.date) {
          label += ' do ' + dateTo.date + ' ' + (dateTo.time || '');
        }

        return {
          ...opt,
          label: opt.label + ' ' + label.trim(),
        };
      } else {
        return { ...opt };
      }
    });
  }

  private createCustomDateForm() {
    const dateFromControl = new FormControl();
    const timeFromControl = new FormControl({ value: '', disabled: true });

    const dateFromForm = new FormGroup({
      date: dateFromControl,
      time: timeFromControl,
    });

    dateFromControl.valueChanges.subscribe(value => {
      if (value) {
        timeFromControl.enable();
        timeFromControl.setValue('00:00');
      } else {
        timeFromControl.disable();
        timeFromControl.setValue('');
      }
    });

    const timeToControl = new FormControl({ value: '', disabled: true });
    const dateToControl = new FormControl('');

    const dateToForm = new FormGroup({
      date: dateToControl,
      time: timeToControl,
    });

    dateFromControl.setValidators([dateToValidation(dateFromForm, dateToForm)]);
    timeFromControl.setValidators([timeValidation, dateToValidation(dateFromForm, dateToForm)]);
    dateToControl.setValidators([dateToValidation(dateFromForm, dateToForm)]);
    timeToControl.setValidators([timeValidation, dateToValidation(dateFromForm, dateToForm)]);

    dateToControl.valueChanges.subscribe(value => {
      if (value) {
        timeToControl.enable();
        timeToControl.setValue('23:59');
      } else {
        timeToControl.disable();
        timeToControl.setValue('');
      }
    });

    return new FormGroup({
      dateFrom: dateFromForm,
      dateTo: dateToForm,
    });
  }

  private createFormDateAsISOString(formDateValue: { date: string; time: string }) {
    const [year, monthPlusOne, day] = formDateValue.date.split('-');

    if (!formDateValue.time) {
      return new Date(Date.UTC(+year, +monthPlusOne - 1, +day)).toISOString();
    }

    const [hours, minutes] = formDateValue.time.split(':');

    return new Date(Date.UTC(+year, +monthPlusOne - 1, +day, +hours, +minutes)).toISOString();
  }
}
